/**
 * Website Studio — /projects/:projectId/website
 *
 * The one place a customer builds and evolves their website:
 *   • no site yet        → the brief (same form as the welcome wizard) + Build
 *   • run in progress    → live progress (timeline + activity + credits)
 *   • site is live       → preview + "Refine with AI" chat, history, credits
 *
 * Repos, workflows and tokens never appear here — the customer sees their
 * website, their words, and the agent's progress.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Bot,
  Check,
  ExternalLink,
  Globe,
  History,
  Loader2,
  RefreshCw,
  RotateCcw,
  Send,
  Sparkles,
  User,
} from 'lucide-react';
import { studioHelpers, type StudioRun } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { Button } from '@/components/ui/button';
import { DataState } from '@/components/common/DataState';
import { useConfirm } from '@/components/ui/confirm-provider';
import { AiCreditsCard, TopUpDialog, useAiCredits } from '@/components/studio/AiCreditsCard';
import {
  BriefForm,
  briefIsReady,
  emptyBrief,
  type BriefFormValue,
} from '@/components/studio/BriefForm';
import { RunProgress } from '@/components/studio/RunProgress';

const TERMINAL = new Set(['done', 'failed', 'cancelled', 'needs_credits']);

function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error
    ?.code;
}

export default function WebsiteStudioPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const credits = useAiCredits();
  const [topUp, setTopUp] = useState<{ open: boolean; reason?: string }>({ open: false });

  const overview = useQuery({
    queryKey: qk.studio(projectId),
    queryFn: () => studioHelpers.overview(projectId!),
    enabled: Boolean(projectId),
    refetchInterval: (q) => (q.state.data?.activeRun ? 15_000 : false),
  });

  // Which run to watch: ?run=… (just started) or the active one.
  const watchedRunId = search.get('run') || overview.data?.activeRun?.id || null;
  const watchedRun = useMemo(
    () =>
      overview.data?.runs.find((r) => r.id === watchedRunId) ?? overview.data?.activeRun ?? null,
    [overview.data, watchedRunId]
  );

  const [brief, setBrief] = useState<BriefFormValue>(emptyBrief());
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !overview.data) return;
    seeded.current = true;
    setBrief(emptyBrief(overview.data.project.intake));
  }, [overview.data]);

  const startRun = useMutation({
    mutationFn: (body: {
      kind: 'generate' | 'edit';
      prompt: string;
      confirmReplace?: boolean;
      resumeRunId?: string;
    }) =>
      studioHelpers.startRun(projectId!, {
        ...body,
        intake: body.kind === 'generate' ? brief.intake : undefined,
      }),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: qk.studio(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.aiCredits() });
      setSearch({ run: run.id }, { replace: true });
    },
    onError: async (err, body) => {
      const code = errorCode(err);
      if (code === 'AI_CREDITS_REQUIRED') {
        setTopUp({ open: true, reason: apiErrorMessage(err) });
        return;
      }
      if (code === 'REQUIRES_CONFIRMATION') {
        const ok = await confirm({
          title: 'Rebuild your website?',
          message:
            'Your current website will be replaced by a new one built from this brief. The current version is kept as a restore point.',
          confirmLabel: 'Rebuild',
          danger: true,
        });
        if (ok) startRun.mutate({ ...body, confirmReplace: true });
        return;
      }
      toast.error(apiErrorMessage(err, 'Could not start'));
    },
  });

  const [chat, setChat] = useState('');
  const sendEdit = () => {
    const prompt = chat.trim();
    if (prompt.length < 3) return;
    setChat('');
    startRun.mutate({ kind: 'edit', prompt });
  };

  return (
    <DataState query={overview}>
      {(data) => {
        const { project, activeRun, runs } = data;
        const showProgress = Boolean(
          watchedRun && (!TERMINAL.has(watchedRun.phase) || search.get('run'))
        );
        const editRuns = runs.filter((r) => r.kind === 'edit' || r.kind === 'generate');
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Link
                  to={`/projects/${projectId}`}
                  className="text-muted-foreground mb-1 inline-flex items-center gap-1 text-xs hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" /> {project.name}
                </Link>
                <h1 className="text-2xl font-bold">Your website</h1>
                <a
                  href={project.customDomain ? `https://${project.customDomain}` : project.siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary mt-1 inline-flex items-center gap-1 text-sm hover:underline"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {(project.customDomain || project.siteUrl).replace(/^https?:\/\//, '')}
                  <ExternalLink className="h-3 w-3" />
                </a>
                {project.hasSite && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {project.deployed
                      ? `Live${project.lastDeployedAt ? ` · updated ${new Date(project.lastDeployedAt).toLocaleString()}` : ''}`
                      : project.buildStatus === 'building'
                        ? 'Publishing your latest changes…'
                        : project.buildStatus === 'failed'
                          ? 'The last publish failed — ask for a fix below.'
                          : 'Saved, not yet published'}
                  </p>
                )}
              </div>
              <div className="w-full sm:w-72">
                <AiCreditsCard compact />
              </div>
            </div>

            {!data.runnerAvailable && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                Website building is temporarily unavailable. Your brief is saved — try again in a
                little while.
              </div>
            )}

            {/* ── Live run ── */}
            {showProgress && watchedRun && (
              <section className="space-y-3">
                <RunProgress
                  projectId={projectId!}
                  runId={watchedRun.id}
                  initialRun={watchedRun}
                  onFinished={() => {
                    queryClient.invalidateQueries({ queryKey: qk.studio(projectId) });
                    queryClient.invalidateQueries({ queryKey: qk.aiCredits() });
                  }}
                  onTopUp={() =>
                    setTopUp({
                      open: true,
                      reason: 'Top up to continue this run where it left off.',
                    })
                  }
                />
                {TERMINAL.has(watchedRun.phase) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {watchedRun.phase === 'needs_credits' && (
                      <Button
                        size="sm"
                        disabled={startRun.isPending}
                        onClick={() =>
                          startRun.mutate({
                            kind: watchedRun.kind,
                            prompt: watchedRun.prompt,
                            resumeRunId: watchedRun.id,
                            confirmReplace: watchedRun.kind === 'generate' ? true : undefined,
                          })
                        }
                      >
                        <RotateCcw className="mr-1.5 h-4 w-4" /> Continue where it left off
                      </Button>
                    )}
                    {watchedRun.phase === 'failed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={startRun.isPending}
                        onClick={() =>
                          startRun.mutate({
                            kind: watchedRun.kind,
                            prompt: watchedRun.prompt,
                            confirmReplace: watchedRun.kind === 'generate' ? true : undefined,
                          })
                        }
                      >
                        <RefreshCw className="mr-1.5 h-4 w-4" /> Try again
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSearch({}, { replace: true })}
                    >
                      <Check className="mr-1.5 h-4 w-4" /> Done
                    </Button>
                  </div>
                )}
              </section>
            )}

            {/* ── No site yet: the brief ── */}
            {!project.hasSite && !activeRun && !showProgress && (
              <section className="bg-card rounded-xl border p-6">
                <div className="mb-5">
                  <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <Sparkles className="text-primary h-5 w-5" /> Tell us what to build
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Answer what you can — anything you skip is left out of the site rather than made
                    up. Building usually takes 10–25 minutes and you'll see every step.
                  </p>
                </div>
                <BriefForm value={brief} onChange={setBrief} disabled={startRun.isPending} />
                <div className="mt-6 flex items-center justify-between gap-3 border-t pt-5">
                  <p className="text-muted-foreground text-xs">
                    Needs {data.credits.reserveFor.generate.toLocaleString()} AI credits to start ·
                    you have {(credits.data?.available ?? data.credits.available).toLocaleString()}
                  </p>
                  <Button
                    disabled={!briefIsReady(brief) || startRun.isPending || !data.runnerAvailable}
                    onClick={() => startRun.mutate({ kind: 'generate', prompt: brief.prompt })}
                  >
                    {startRun.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1.5 h-4 w-4" />
                    )}
                    Build my website
                  </Button>
                </div>
              </section>
            )}

            {/* ── Live site: preview + refine ── */}
            {project.hasSite && !activeRun && (
              <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
                <section className="bg-card overflow-hidden rounded-xl border">
                  <div className="flex items-center justify-between border-b px-4 py-2 text-sm">
                    <span className="text-muted-foreground truncate">
                      {project.siteUrl.replace(/^https?:\/\//, '')}
                    </span>
                    <a
                      href={project.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  {project.deployed ? (
                    <iframe
                      title="Your website"
                      src={project.siteUrl}
                      className="h-[640px] w-full bg-white"
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    />
                  ) : (
                    <div className="text-muted-foreground flex h-[320px] items-center justify-center text-sm">
                      {project.buildStatus === 'building' ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Publishing…
                        </span>
                      ) : (
                        'Your website will appear here once it is published.'
                      )}
                    </div>
                  )}
                </section>

                <section className="bg-card flex flex-col rounded-xl border">
                  <div className="border-b px-4 py-3">
                    <h2 className="flex items-center gap-2 font-semibold">
                      <Bot className="text-primary h-4 w-4" /> Refine with AI
                    </h2>
                    <p className="text-muted-foreground text-xs">
                      Describe a change in plain words. Each change is applied, checked and
                      published for you.
                    </p>
                  </div>
                  <div className="max-h-[440px] flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
                    {editRuns.length === 0 && (
                      <p className="text-muted-foreground">No changes yet.</p>
                    )}
                    {[...editRuns].reverse().map((r) => (
                      <RunBubble
                        key={r.id}
                        run={r}
                        onOpen={() => setSearch({ run: r.id }, { replace: true })}
                      />
                    ))}
                  </div>
                  <div className="border-t p-3">
                    <div className="flex flex-wrap gap-1.5 pb-2">
                      {[
                        'Make the hero headline punchier',
                        'Add a WhatsApp button to every page',
                        'Use warmer colours',
                      ].map((s) => (
                        <button
                          key={s}
                          className="bg-muted hover:bg-muted/70 rounded-full px-2.5 py-1 text-xs"
                          onClick={() => setChat(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-end gap-2">
                      <textarea
                        className="bg-background max-h-32 min-h-[44px] flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none"
                        rows={2}
                        value={chat}
                        placeholder="e.g. Add an FAQ section about delivery times"
                        onChange={(e) => setChat(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendEdit();
                          }
                        }}
                        disabled={startRun.isPending || !data.runnerAvailable}
                      />
                      <Button
                        size="sm"
                        onClick={sendEdit}
                        disabled={
                          chat.trim().length < 3 || startRun.isPending || !data.runnerAvailable
                        }
                      >
                        {startRun.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-muted-foreground mt-1.5 text-[11px]">
                      About {data.credits.reserveFor.edit.toLocaleString()} credits to start an edit
                      · charged for what it uses
                    </p>
                  </div>
                </section>
              </div>
            )}

            {/* ── History ── */}
            {runs.length > 0 && (
              <section className="bg-card rounded-xl border">
                <div className="flex items-center gap-2 border-b px-4 py-3">
                  <History className="text-muted-foreground h-4 w-4" />
                  <h2 className="font-semibold">History</h2>
                </div>
                <ul className="divide-y">
                  {runs.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate">
                          <span
                            className={`mr-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${r.kind === 'edit' ? 'bg-sky-100 text-sky-800' : 'bg-violet-100 text-violet-800'}`}
                          >
                            {r.kind === 'edit' ? 'Change' : 'Build'}
                          </span>
                          {r.prompt}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {new Date(r.createdAt).toLocaleString()} · {r.phaseLabel} ·{' '}
                          {(r.credits.charged || r.credits.used).toLocaleString()} credits
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearch({ run: r.id }, { replace: true })}
                      >
                        View
                      </Button>
                    </li>
                  ))}
                </ul>
                {project.hasSite && !activeRun && (
                  <div className="border-t px-4 py-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        startRun.mutate({
                          kind: 'generate',
                          prompt:
                            brief.prompt || runs.find((r) => r.kind === 'generate')?.prompt || '',
                        })
                      }
                      disabled={startRun.isPending}
                    >
                      <RefreshCw className="mr-1.5 h-4 w-4" /> Start over with a new build
                    </Button>
                  </div>
                )}
              </section>
            )}

            <TopUpDialog
              open={topUp.open}
              onOpenChange={(o) => setTopUp({ open: o })}
              wallet={credits.data}
              reason={topUp.reason}
            />
          </div>
        );
      }}
    </DataState>
  );
}

function RunBubble({ run, onOpen }: { run: StudioRun; onOpen: () => void }) {
  const done = TERMINAL.has(run.phase);
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <User className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
        <p className="bg-muted rounded-lg px-3 py-2">{run.prompt}</p>
      </div>
      <div className="flex items-start gap-2">
        <Bot className="text-primary mt-0.5 h-4 w-4 shrink-0" />
        <div className="rounded-lg border px-3 py-2">
          {!done && (
            <p className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {run.phaseLabel}…
            </p>
          )}
          {done && run.summary && <p className="whitespace-pre-line">{run.summary}</p>}
          {done && !run.summary && <p className="text-muted-foreground">{run.phaseLabel}</p>}
          {run.error && <p className="mt-1 text-xs text-red-600">{run.error.message}</p>}
          <button className="text-primary mt-1 text-xs hover:underline" onClick={onOpen}>
            {done ? 'See what changed' : 'Watch progress'}
          </button>
        </div>
      </div>
    </div>
  );
}
